import { isOutlookFiled, normalizeEmailFolder } from './emailFolder';
import type { EmailFolder } from './emailFolder';
import { moveEmail, restoreEmail } from './mailActions';
import type { EmailLocation, MoveDestination, MoveResult, MoveSubject } from './mailActions';

/**
 * The pure parts of the feed's archive / delete / restore / bulk-select flows.
 *
 *  - planMoves:      which emails a move applies to (movable folder, not already there, deduped)
 *  - runMoves:       run the moves with a concurrency cap, aggregate per-email outcomes
 *  - undoMoves:      put the succeeded ones back (one undo for a whole batch)
 *  - moveSummary:    the one toast line for a batch, partial failures included
 *  - pending moves:  the optimistic overlay (hide while in flight, show at the confirmed
 *                    location until the Firestore snapshot catches up)
 *  - selection:      toggle / range / prune over email ids
 *
 * Nothing here touches React or Firestore. The only side effects are the injectable `move` /
 * `restore` calls (default: mailActions.moveEmail / restoreEmail), which never throw and never
 * toast - this module does not toast either; the feed decides what to show.
 */

// ---------------------------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------------------------

/** The fields of a feed row these helpers read. A whole FeedEmailData fits. */
export interface MoveRow {
  id: string;
  folder?: unknown;
  deleted?: unknown;
  emailType?: unknown;
  quarantined?: unknown;
  outlookFolderId?: unknown;
  outlookFolderName?: unknown;
  outlookFolderPath?: unknown;
}

const text = (v: unknown): string | undefined => (typeof v === 'string' && v.trim() ? v.trim() : undefined);

/**
 * The exact shape mailActions wants. Built field by field (instead of passing the row) because
 * a feed row can carry `quarantined: null`, which MoveSubject does not accept, and because it
 * keeps every other row field (bodies, addresses) away from the move call.
 */
export function toMoveSubject(e: MoveRow): MoveSubject {
  return {
    id: e.id,
    folder: text(e.folder),
    deleted: e.deleted === true,
    emailType: text(e.emailType),
    quarantined: e.quarantined === true,
    outlookFolderId: text(e.outlookFolderId),
    outlookFolderName: text(e.outlookFolderName),
    outlookFolderPath: text(e.outlookFolderPath),
  };
}

/** Folders a message can be moved out of (the server enforces the same list). */
export const MOVABLE_FOLDERS: ReadonlySet<EmailFolder> = new Set<EmailFolder>(['inbox', 'junk', 'archive', 'filed', 'deleted']);

export const isMovableEmail = (e: MoveRow): boolean => MOVABLE_FOLDERS.has(normalizeEmailFolder(e));

/** Is the email already where `destination` would put it? ('archive' = Archive or any filed folder.) */
export function alreadyAt(e: MoveRow, destination: MoveDestination): boolean {
  const f = normalizeEmailFolder(e);
  switch (destination) {
    case 'archive': return isOutlookFiled(f);
    case 'inbox': return f === 'inbox';
    case 'junk': return f === 'junk';
    case 'deleted': return f === 'deleted';
    default: return false;
  }
}

/** Same folder and, for filed mail, the same Outlook folder. */
export function locationSignature(e: MoveRow): string {
  const f = normalizeEmailFolder(e);
  return f === 'filed' ? `filed:${text(e.outlookFolderId) || ''}` : f;
}

const sameLocation = (a: EmailLocation, b: EmailLocation): boolean =>
  a.folder === b.folder && (a.folder !== 'filed' || (a.outlookFolderId || '') === (b.outlookFolderId || ''));

// ---------------------------------------------------------------------------------------------
// Planning
// ---------------------------------------------------------------------------------------------

export interface MovePlan<E> {
  movable: E[];
  skipped: Array<{ email: E; reason: string }>;
}

const DEST_LABEL: Record<MoveDestination, string> = { archive: 'archive', inbox: 'inbox', junk: 'junk', deleted: 'deleted' };

/**
 * Split `emails` into what `destination` applies to and what it does not. Duplicates (a thread
 * expanded next to its own primary row) collapse to one. `skipIds` holds ids already being moved.
 */
export function planMoves<E extends MoveRow>(
  emails: ReadonlyArray<E>,
  destination: MoveDestination,
  opts: { skipIds?: ReadonlySet<string> } = {},
): MovePlan<E> {
  const plan: MovePlan<E> = { movable: [], skipped: [] };
  const seen = new Set<string>();
  for (const email of emails) {
    const id = email?.id;
    if (!id || seen.has(id)) continue;
    seen.add(id);
    if (opts.skipIds && opts.skipIds.has(id)) {
      plan.skipped.push({ email, reason: 'already being moved' });
    } else if (!isMovableEmail(email)) {
      plan.skipped.push({ email, reason: 'sent and draft messages cannot be moved' });
    } else if (alreadyAt(email, destination)) {
      plan.skipped.push({ email, reason: `already in ${DEST_LABEL[destination]}` });
    } else {
      plan.movable.push(email);
    }
  }
  return plan;
}

// ---------------------------------------------------------------------------------------------
// Running
// ---------------------------------------------------------------------------------------------

/** Map with at most `limit` calls in flight; results keep the input order. `fn` must not throw. */
export async function mapLimit<T, R>(
  items: ReadonlyArray<T>,
  limit: number,
  fn: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  const results = new Array<R>(items.length);
  let next = 0;
  const width = Math.min(Math.max(1, Math.floor(limit) || 1), items.length);
  const workers = Array.from({ length: width }, async () => {
    for (;;) {
      const i = next++;
      if (i >= items.length) return;
      results[i] = await fn(items[i], i);
    }
  });
  await Promise.all(workers);
  return results;
}

export type MoveOk = Extract<MoveResult, { ok: true }>;
export interface MoveSuccess<E> { email: E; result: MoveOk }
export interface MoveFailure<E> { email: E; reason: string; forbidden?: boolean }
export interface RunMovesResult<E> { succeeded: Array<MoveSuccess<E>>; failed: Array<MoveFailure<E>> }

export interface RunMovesOptions {
  /** Moves in flight at once. Default 4 (Graph throttles; the server answers each in ~1s). */
  concurrency?: number;
  /** Injectable for tests. Default: mailActions.moveEmail. */
  move?: (subject: MoveSubject, destination: MoveDestination) => Promise<MoveResult>;
  /** Called after each email settles: (settled so far, total). Never throws into the run. */
  onProgress?: (done: number, total: number) => void;
}

const DEFAULT_CONCURRENCY = 4;
const reasonOf = (r: unknown): string => text((r as { reason?: unknown } | null)?.reason) || 'unknown error';

function tellProgress(cb: RunMovesOptions['onProgress'], done: number, total: number): void {
  if (!cb) return;
  try { cb(done, total); } catch { /* a UI callback must not break the batch */ }
}

/**
 * Move every item to `destination`. Per-email outcomes, input order. If the server says the
 * caller is not the owner (forbidden), that is true for every message: the rest are not sent
 * and are reported failed with the same reason, instead of firing N doomed requests.
 */
export async function runMoves<E extends MoveRow>(
  items: ReadonlyArray<E>,
  destination: MoveDestination,
  opts: RunMovesOptions = {},
): Promise<RunMovesResult<E>> {
  const move = opts.move || moveEmail;
  const total = items.length;
  let settled = 0;
  let forbidden: string | null = null;

  type Outcome = { ok: true; result: MoveOk } | { ok: false; reason: string; forbidden?: boolean };
  const outcomes = await mapLimit<E, Outcome>(items, opts.concurrency ?? DEFAULT_CONCURRENCY, async (email) => {
    let out: Outcome;
    if (forbidden !== null) {
      out = { ok: false, reason: forbidden, forbidden: true };
    } else {
      try {
        const res = await move(toMoveSubject(email), destination);
        if (res && res.ok) out = { ok: true, result: res };
        else {
          const f = res && !res.ok && res.forbidden === true;
          out = { ok: false, reason: reasonOf(res), ...(f ? { forbidden: true } : {}) };
          if (f) forbidden = out.reason;
        }
      } catch {
        // moveEmail never throws; if an injected mover does, say so without logging the error.
        out = { ok: false, reason: 'unknown error' };
      }
    }
    settled += 1;
    tellProgress(opts.onProgress, settled, total);
    return out;
  });

  const result: RunMovesResult<E> = { succeeded: [], failed: [] };
  outcomes.forEach((o, i) => {
    if (o.ok) result.succeeded.push({ email: items[i], result: o.result });
    else result.failed.push({ email: items[i], reason: o.reason, ...(o.forbidden ? { forbidden: true } : {}) });
  });
  return result;
}

/** Can a move be undone by moving back? (Out of Deleted Items, and to an unknown folder, cannot.) */
function canRestoreTo(previous: EmailLocation): boolean {
  switch (previous.folder) {
    case 'inbox':
    case 'junk':
    case 'archive':
      return true;
    case 'filed':
      return !!previous.outlookFolderId;
    default:
      return false;
  }
}

/** The succeeded moves an undo can actually reverse (something moved, and it can go back). */
export function undoableMoves<E>(succeeded: ReadonlyArray<MoveSuccess<E>>): Array<MoveSuccess<E>> {
  return succeeded.filter((s) => !sameLocation(s.result.previous, s.result.current) && canRestoreTo(s.result.previous));
}

export interface UndoMovesOptions {
  concurrency?: number;
  /** Injectable for tests. Default: mailActions.restoreEmail. */
  restore?: (subject: MoveSubject, previous: EmailLocation) => Promise<MoveResult>;
}

/** Put each message back where its move said it came from. */
export async function undoMoves<E extends MoveRow>(
  items: ReadonlyArray<MoveSuccess<E>>,
  opts: UndoMovesOptions = {},
): Promise<{ restored: E[]; failed: Array<MoveFailure<E>> }> {
  const restore = opts.restore || restoreEmail;
  const outcomes = await mapLimit(items, opts.concurrency ?? DEFAULT_CONCURRENCY, async (s) => {
    try {
      const res = await restore(toMoveSubject(s.email), s.result.previous);
      return res && res.ok ? null : { reason: reasonOf(res), forbidden: !!(res && !res.ok && res.forbidden) };
    } catch {
      return { reason: 'unknown error', forbidden: false };
    }
  });
  const out: { restored: E[]; failed: Array<MoveFailure<E>> } = { restored: [], failed: [] };
  outcomes.forEach((o, i) => {
    if (o === null) out.restored.push(items[i].email);
    else out.failed.push({ email: items[i].email, reason: o.reason, ...(o.forbidden ? { forbidden: true } : {}) });
  });
  return out;
}

// ---------------------------------------------------------------------------------------------
// Toast text
// ---------------------------------------------------------------------------------------------

export type SummaryTone = 'success' | 'warning' | 'error';

/** The distinct failure reasons, first few only: "a; b; +2 more". */
export function joinReasons(failed: ReadonlyArray<{ reason: string }>, max = 2): string {
  const seen: string[] = [];
  for (const f of failed) if (!seen.includes(f.reason)) seen.push(f.reason);
  const shown = seen.slice(0, max).join('; ');
  return seen.length > max ? `${shown}; +${seen.length - max} more` : shown;
}

/**
 * The single toast line for a batch: what happened, and what did not. One email reads
 * 'archived' / 'moved to deleted'; a batch reads 'archived 12' / 'moved 12 to deleted'.
 */
export function moveSummary(
  destination: MoveDestination,
  succeeded: number,
  failed: ReadonlyArray<{ reason: string }>,
): { message: string; tone: SummaryTone } {
  const one = succeeded === 1;
  const done =
    destination === 'archive' ? (one ? 'archived' : `archived ${succeeded}`)
    : destination === 'deleted' ? (one ? 'moved to deleted' : `moved ${succeeded} to deleted`)
    : destination === 'inbox' ? (one ? 'moved to inbox' : `moved ${succeeded} to inbox`)
    : (one ? 'moved to junk' : `moved ${succeeded} to junk`);
  if (failed.length === 0) return { message: done, tone: 'success' };

  if (succeeded === 0) {
    const verb = destination === 'archive' ? 'archive'
      : destination === 'deleted' ? 'delete'
      : destination === 'inbox' ? 'move to inbox'
      : 'move to junk';
    const n = failed.length > 1 ? ` ${failed.length}` : '';
    return { message: `couldn't ${verb}${n}: ${joinReasons(failed)}`, tone: 'error' };
  }
  return { message: `${done}, ${failed.length} failed: ${joinReasons(failed)}`, tone: 'warning' };
}

const shortTitle = (t: unknown): string => {
  const s = (typeof t === 'string' ? t : '').replace(/\s+/g, ' ').trim() || '(no subject)';
  return s.length > 40 ? `${s.slice(0, 39)}...` : s;
};

/** The error line when an undo could not restore everything: names the messages, then the reason. */
export function undoFailureText(failed: ReadonlyArray<{ email: { title?: unknown; subject?: unknown }; reason: string }>): string {
  const names = failed.slice(0, 3).map((f) => `"${shortTitle(f.email.title || f.email.subject)}"`);
  const more = failed.length > 3 ? ` and ${failed.length - 3} more` : '';
  return `couldn't put back ${names.join(', ')}${more}: ${joinReasons(failed)}`;
}

// ---------------------------------------------------------------------------------------------
// Pending moves (the optimistic overlay)
// ---------------------------------------------------------------------------------------------

/**
 * 'purge' = delete forever: nothing to show at a new location, the row just goes.
 * inflight  the request is out: the row is HIDDEN (and out of the counts) right away.
 * applied   the server said ok and gave the new location: the row is shown THERE until the
 *           Firestore snapshot catches up (its location changes from `fromSig`).
 */
export type PendingDestination = MoveDestination | 'purge';
export interface PendingMove {
  destination: PendingDestination;
  phase: 'inflight' | 'applied';
  at: number;
  /** locationSignature of the row when the move started: a different signature = the data caught up. */
  fromSig: string;
  /** Where the server put it (applied moves only). */
  current?: EmailLocation;
}
export type PendingMap = Readonly<Record<string, PendingMove>>;

/** An applied entry waits this long for its snapshot (only for mail the live feed covers). */
export const APPLIED_TTL_MS = 30 * 1000;
/**
 * An in-flight entry that has seen no sign of life for this long stops hiding its row: a request
 * that hangs cannot hide mail. (touchInflight is the sign of life: a batch calls it whenever one
 * of its moves settles, so a long bulk batch is not timed out while it is still making progress.)
 */
export const INFLIGHT_MAX_MS = 30 * 1000;
const TOUCH_MIN_GAP_MS = 5 * 1000;

function patchedAt<E extends MoveRow>(e: E, loc: EmailLocation): E {
  return {
    ...e,
    folder: loc.folder,
    deleted: loc.deleted,
    outlookFolderId: loc.outlookFolderId,
    outlookFolderName: loc.outlookFolderName,
    outlookFolderPath: loc.outlookFolderPath,
  };
}

/**
 * The rows as the optimistic overlay says they are: in-flight and purged rows gone, applied
 * rows at their confirmed location (until the real data changes). Returns `emails` itself when
 * there is nothing pending.
 */
export function applyPendingMoves<E extends MoveRow>(emails: E[], pending: PendingMap): E[] {
  if (Object.keys(pending).length === 0) return emails;
  const out: E[] = [];
  for (const e of emails) {
    const p = pending[e.id];
    if (!p) { out.push(e); continue; }
    if (p.destination === 'purge' || p.phase === 'inflight') continue;
    if (locationSignature(e) !== p.fromSig) { out.push(e); continue; } // the data caught up
    if (p.current) out.push(patchedAt(e, p.current));
  }
  return out;
}

/**
 * Refresh the clock of the still-in-flight entries among `ids` (a batch showing progress).
 * Entries touched within the last 5s are left alone, so a fast batch does not make the feed
 * recompute its whole list on every settled move.
 */
export function touchInflight(pending: PendingMap, ids: ReadonlyArray<string>, now: number): PendingMap {
  let next: Record<string, PendingMove> | null = null;
  for (const id of ids) {
    const p = pending[id];
    if (!p || p.phase !== 'inflight' || now - p.at < TOUCH_MIN_GAP_MS) continue;
    if (!next) next = { ...pending };
    next[id] = { ...p, at: now };
  }
  return next || pending;
}

/**
 * Drop the entries that are finished: the data caught up, the email is gone, or they timed out.
 * `byId` must hold the RAW rows (not the overlaid ones). Returns `pending` itself when nothing
 * was dropped.
 *
 * Mail outside the live snapshot window (search hits) never gets a snapshot, so an applied
 * entry for it is kept - it is what keeps such a row at its new location - until the row is gone
 * or its data changes. Purge entries are kept while the row exists (the server leaves a
 * permanently deleted message's doc in place as 'deleted').
 */
export function sweepPending(
  pending: PendingMap,
  byId: ReadonlyMap<string, MoveRow>,
  now: number,
  isLive: (id: string) => boolean,
): PendingMap {
  let next: Record<string, PendingMove> | null = null;
  for (const id of Object.keys(pending)) {
    const p = pending[id];
    const e = byId.get(id);
    let drop: boolean;
    if (p.destination === 'purge') drop = !e;
    else if (p.phase === 'inflight') drop = now - p.at > INFLIGHT_MAX_MS;
    else drop = !e || locationSignature(e) !== p.fromSig || (isLive(id) && now - p.at > APPLIED_TTL_MS);
    if (drop) {
      if (!next) next = { ...pending };
      delete next[id];
    }
  }
  return next || pending;
}

// ---------------------------------------------------------------------------------------------
// Selection (by email id)
// ---------------------------------------------------------------------------------------------

export type SelectionState = 'none' | 'some' | 'all';

export function selectionState(selected: ReadonlySet<string>, ids: ReadonlyArray<string>): SelectionState {
  if (ids.length === 0) return 'none';
  let n = 0;
  for (const id of ids) if (selected.has(id)) n += 1;
  return n === 0 ? 'none' : n === ids.length ? 'all' : 'some';
}

/** Select `ids` when any is unselected, else unselect them all (a thread row toggles as one). */
export function toggleIds(selected: ReadonlySet<string>, ids: ReadonlyArray<string>): Set<string> {
  const next = new Set(selected);
  if (selectionState(selected, ids) === 'all') ids.forEach((id) => next.delete(id));
  else ids.forEach((id) => next.add(id));
  return next;
}

export function addIds(selected: ReadonlySet<string>, ids: ReadonlyArray<string>): Set<string> {
  const next = new Set(selected);
  ids.forEach((id) => next.add(id));
  return next;
}

/** Keep only the selected ids that still exist. Returns `selected` itself when nothing was dropped. */
export function pruneSelection(selected: ReadonlySet<string>, present: ReadonlySet<string>): ReadonlySet<string> {
  let next: Set<string> | null = null;
  selected.forEach((id) => {
    if (present.has(id)) return;
    if (!next) next = new Set(selected);
    next.delete(id);
  });
  return next || selected;
}

/** The row keys from `anchor` to `target` inclusive, in list order. No usable anchor = just the target. */
export function rangeKeys(keys: ReadonlyArray<string>, anchor: string | null | undefined, target: string): string[] {
  const t = keys.indexOf(target);
  if (t < 0) return [];
  const a = anchor ? keys.indexOf(anchor) : -1;
  if (a < 0) return [target];
  return keys.slice(Math.min(a, t), Math.max(a, t) + 1);
}

/**
 * Where the cursor goes after rows were removed from under it: the next row that stays, else
 * the nearest one above, else nothing. `index` is the cursor's position in `keys` (-1 = none).
 */
export function nextCursorKey(keys: ReadonlyArray<string>, index: number, removed: ReadonlySet<string>): string | null {
  for (let i = Math.max(0, index + 1); i < keys.length; i++) if (!removed.has(keys[i])) return keys[i];
  for (let i = Math.min(index, keys.length) - 1; i >= 0; i--) if (!removed.has(keys[i])) return keys[i];
  return null;
}
